import React, { useState } from "react";
import api, { API } from "@/api";
import { useI18n } from "@/i18n";
import { PageHeader, Tile } from "@/components/Bits";
import UsersAdmin from "@/components/UsersAdmin";
import { ArrowsClockwise, FileXls } from "@phosphor-icons/react";

export default function Admin() {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const reimport = async () => {
    if (!confirm("This will WIPE & re-import installed_base, opportunities, contacts, visits, account_plans from /app/data. Continue?")) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/reimport");
      setResult(data);
    } catch (e) {
      setResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-page">
      <PageHeader title={t("admin.title")} subtitle={t("admin.subtitle")} />
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
        <UsersAdmin />

        <Tile testId="admin-import">
          <div className="label-tech mb-3">{t("admin.reimport")}</div>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Re-reads <span className="font-mono text-xs">/app/data/crm_v5.1.xlsx</span> and{" "}
            <span className="font-mono text-xs">/app/data/roadmap_v4.1.xlsx</span> and rebuilds all
            collections. The City/Operator swap bug from the source files is fixed automatically.
          </p>
          <button onClick={reimport} disabled={running} className="btn-primary flex items-center gap-2" data-testid="admin-reimport">
            <ArrowsClockwise size={14} className={running ? "animate-spin" : ""} />
            {running ? t("common.loading") : t("admin.reimport")}
          </button>
          {result && (
            <pre className="mt-4 p-3 border border-white/10 bg-black text-xs font-mono text-zinc-300 overflow-auto" data-testid="reimport-result">
              {JSON.stringify(result, null, 2)}
            </pre>
          )}
        </Tile>

        <Tile testId="admin-export">
          <div className="label-tech mb-3">Excel exports</div>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            One-click download — rebuild both source workbooks from the live database, with the
            canonical City/Operator fix already applied.
          </p>
          <div className="flex gap-2">
            <a
              href={`${API}/export/crm.xlsx`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost flex items-center gap-2"
              data-testid="export-crm"
            >
              <FileXls size={14} /> {t("admin.exportCrm")}
            </a>
            <a
              href={`${API}/export/roadmap.xlsx`}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost flex items-center gap-2"
              data-testid="export-roadmap"
            >
              <FileXls size={14} /> {t("admin.exportRoadmap")}
            </a>
          </div>
        </Tile>

        <Tile className="lg:col-span-2" testId="admin-integrity">
          <div className="label-tech mb-3">{t("admin.dataIntegrity")}</div>
          <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
            <p>
              On import, the following City/Operator corrections are applied to the raw data —
              this fixes a swap bug present in both Excel source files:
            </p>
            <table className="tech text-xs mt-3">
              <thead>
                <tr><th>Raw City</th><th>Raw Operator</th><th>Canonical City</th><th>Canonical Operator</th></tr>
              </thead>
              <tbody>
                <tr><td>METRO</td><td>ISTANBUL</td><td>Istanbul</td><td>Metro Istanbul</td></tr>
                <tr><td>TCDD</td><td>TAŞIMACILIK</td><td>Multi-city (national)</td><td>TCDD Taşımacılık</td></tr>
                <tr><td>ARKAS</td><td>RAIL</td><td>İzmir</td><td>Arkas Rail</td></tr>
                <tr><td>KÖRFEZ ULAŞTIRMA</td><td>—</td><td>Kocaeli</td><td>Körfez Ulaştırma</td></tr>
                <tr><td>KONYA</td><td>—</td><td>Konya</td><td>Konya Tramvay</td></tr>
              </tbody>
            </table>
          </div>
        </Tile>
      </div>
    </div>
  );
}
